import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CLIENT_PORTAL_TOKEN_KEY = "client_portal_token";
export const CLIENT_PORTAL_TENANT_KEY = "client_portal_slug";

function formatCpf(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

export default function PortalClientLogin() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [cpf, setCpf] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/booking/${slug}/info`);
        setTenant(data);
        if (data.primary_color) {
          document.documentElement.style.setProperty("--tenant-primary", data.primary_color);
        }
      } catch (e) {
        // continua silenciosamente — login ainda funciona
      }
    })();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const digits = cpf.replace(/\D/g, "");
      const { data } = await axios.post(`${API}/portal/client/auth/cpf-login`, {
        tenant_slug: slug,
        cpf: digits,
      });
      localStorage.setItem(CLIENT_PORTAL_TOKEN_KEY, data.access_token);
      localStorage.setItem(CLIENT_PORTAL_TENANT_KEY, slug);
      toast.success(`Olá, ${data.client.name.split(" ")[0]}!`);
      navigate(`/portal/${slug}/cliente/home`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CPF não encontrado.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background via-background to-muted/30">
      <Card className="p-8 max-w-md w-full space-y-6 rounded-2xl shadow-xl">
        {tenant?.logo_url && (
          <img src={tenant.logo_url} alt={tenant.name} className="h-14 w-14 rounded-xl object-cover mx-auto shadow-sm" />
        )}
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Portal do cliente</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
            {tenant?.name || "Acessar minha conta"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Entre apenas com seu CPF — sem senha. Veja agendamentos, pontos e prêmios.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="client-portal-form">
          <div>
            <Label>CPF</Label>
            <Input
              data-testid="client-portal-cpf"
              type="tel"
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
              value={cpf}
              onChange={(e) => setCpf(formatCpf(e.target.value))}
              required
              autoFocus
              className="text-lg tracking-wider font-mono text-center"
            />
          </div>
          <Button
            data-testid="client-portal-submit"
            type="submit"
            disabled={busy || cpf.replace(/\D/g, "").length !== 11}
            className="w-full"
            size="lg"
            style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
            Entrar
          </Button>
        </form>

        <div className="text-xs text-center text-muted-foreground border-t pt-4 space-y-1">
          <div>Não consegue entrar? Peça à recepção para liberar seu acesso.</div>
          <Link to={`/empresa/${slug}`} className="inline-flex items-center gap-1 hover:text-foreground transition mt-2">
            <ArrowLeft className="w-3 h-3" /> Voltar para o site
          </Link>
        </div>
      </Card>
    </div>
  );
}
